import { useMemo, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { ComponentProps } from '@components/strapi/DynamicZoneComponent';
import { PostsSectionDTO } from '@lib/strapi/dtos/components/sections/postsSection.dto';
import { SectionHeading, dataOf } from '../shared-ui';
import { IconDoc } from '@components/utils/icons';

type PostCard = {
  slug?: string; title?: string; teaser?: string; displayDate?: string;
  readingMinutes?: number;
  featuredImage?: { url: string; alternativeText?: string };
  categories?: { name?: string }[];
};

/** «4 λεπτά ανάγνωση · Σεπτέμβριος 2026» — month+year only: the exact day of
 *  an evergreen clinical article is noise, but the season signals freshness. */
const metaLine = (
  p: PostCard,
  locale: string,
  t: (key: string, params?: Record<string, unknown>) => string
): string => {
  const parts: string[] = [];
  if (p.readingMinutes) {
    parts.push(t('blog.minRead', { count: p.readingMinutes }));
  }
  if (p.displayDate) {
    const d = new Date(p.displayDate);
    if (!Number.isNaN(d.getTime())) {
      // timeZone UTC: displayDate parses as UTC midnight — without it, any
      // visitor west of UTC formats the previous day (a Sept-1 post read
      // «Αύγουστος» in the US), and the SSR/client text mismatch threw
      // React #418 on every such visit (root-caused 2026-09-16).
      parts.push(d.toLocaleDateString(locale === 'en' ? 'en-GB' : 'el-GR', { month: 'long', year: 'numeric', timeZone: 'UTC' }));
    }
  }
  return parts.join(' · ');
};

/**
 * Blog listing (item 19): flat cards, image on the card radius, category chip.
 *
 * The category filter (artifact v5's pill bar) is CLIENT-SIDE state over the
 * already-fetched list — with a dozen posts and three categories, per-category
 * routes would add build surface for no reader benefit. It only renders when
 * `showCategories` is on AND there are ≥2 categories: a filter with one option
 * is noise. Pills are buttons, not links — the filtered view is not a page.
 */
const PostsSectionImplementation = ({ section, page }: ComponentProps<PostsSectionDTO>) => {
  const [active, setActive] = useState<string | null>(null);
  const { locale } = useRouter();
  const { t } = useTranslation('main');
  // on an article page, the section is «Διαβάστε επίσης» — the article
  // being read must not recommend itself (owner, 2026-09-08)
  const selfSlug = (page as { entityKind?: string; entity?: { slug?: string } } | undefined)?.entityKind === 'post'
    ? (page as { entity?: { slug?: string } }).entity?.slug
    : undefined;
  const all = ((dataOf(page).posts ?? []) as PostCard[]).filter((p) => !selfSlug || p.slug !== selfSlug);

  const categories = useMemo(() => {
    const names = all.flatMap((p) => (p.categories ?? []).map((c) => c.name).filter(Boolean)) as string[];
    return Array.from(new Set(names));
  }, [all]);

  if (!section) return null;
  const filtered = active ? all.filter((p) => p.categories?.some((c) => c.name === active)) : all;
  const items = filtered.slice(0, section.maxItems || 12);
  if (!all.length) return null;

  // maxItems separates the blog LISTING (seeded 12) from the homepage TEASER
  // (seeded 3): a filter bar on a three-card teaser is clutter, and the teaser
  // must never imply the homepage holds the whole blog.
  const showFilter = Boolean(section.showCategories) && categories.length >= 2 && (section.maxItems || 12) >= 6;

  // With no section heading there is no h2 between the page h1 and the card
  // titles — heading-order violation (Lighthouse a11y, 2026-09-16; same rule
  // as StepsSection). Card titles take the level that follows what exists.
  const CardTitle = section.heading ? 'h3' : 'h2';
  return (
    <section className="my-14 md:my-20">
      <SectionHeading heading={section.heading} intro={section.intro} icon={<IconDoc />} />
      {showFilter ? (
        <div className="mb-8 flex flex-wrap gap-2" role="group" aria-label={t('blog.filterAria')}>
          {[null, ...categories].map((name) => (
            <button key={name ?? '__all'} type="button" onClick={() => setActive(name)}
              aria-pressed={active === name}
              className={`rounded-full px-4 py-1.5 text-sm font-extrabold transition-colors ${
                active === name
                  ? 'bg-violet-700 text-white'
                  : 'border border-violet-200 bg-violet-050 text-violet-700 hover:border-violet-700'
              }`}>
              {name ?? t('blog.all')}
            </button>
          ))}
        </div>
      ) : null}
      <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((p) => (
          <Link key={p.slug} href={`/blog/${p.slug}`} className="group flex flex-col">
            <div className="relative aspect-[16/10] overflow-hidden rounded-2xl bg-violet-050">
              {p.featuredImage ? (
                <Image src={p.featuredImage.url} alt={p.featuredImage.alternativeText ?? p.title ?? ''} fill
                  sizes="(min-width:1024px) 22rem, 90vw" className="clinical-photo transition-transform group-hover:scale-[1.03]" />
              ) : null}
            </div>
            {section.showCategories && p.categories?.length ? (
              <span className="mt-3 self-start rounded-full border border-violet-200 bg-violet-050 px-2.5 py-0.5 text-xs font-extrabold text-violet-700">
                {p.categories[0]?.name}
              </span>
            ) : null}
            <CardTitle className="mt-2 text-lg font-extrabold leading-snug text-ink group-hover:text-violet-700">{p.title}</CardTitle>
            {p.teaser ? <p className="mt-1 line-clamp-2 text-[0.9375rem] text-muted">{p.teaser}</p> : null}
            {metaLine(p, locale ?? 'el', t) ? <p className="mt-2 text-xs text-muted">{metaLine(p, locale ?? 'el', t)}</p> : null}
          </Link>
        ))}
      </div>
      {!items.length ? (
        <p className="text-sm text-muted">{t('blog.empty')}</p>
      ) : null}
      {/* when the section shows a subset (homepage teaser, article footer),
          the door to the full blog is explicit */}
      {items.length && items.length < all.length ? (
        <div className="mt-8">
          <Link href="/blog"
            className="inline-flex items-center gap-1.5 rounded-full border-2 border-violet-700 px-5 py-2.5 text-sm font-extrabold text-violet-700 transition-colors hover:bg-violet-700 hover:text-white">
            {t('blog.viewAll')}
          </Link>
        </div>
      ) : null}
    </section>
  );
};

export default PostsSectionImplementation;
